import { isInIframe } from "components/utils/dom";
import { KeyboardEvent, useMemo, useRef, useState } from "react";
import { Collapse, Table, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { capitalize, orderBy } from "lodash";
import useMediaQuery from "components/utils/useMediaQuery";
import { DataGridHead } from "components/DataGrid/DataGridHead";
import { ReactComponent as FilesPlaceholder } from "./../assets/files-placeholder.svg";
import "./../style.scss";
import { SubmitButton } from "components/Button/SubmitButton";
import { downloadFile, downloadFileFolder } from "components/utils";
import { download } from "components/utils/files";
import { ApplicationFile } from "types/SubmittedAppData";
import { getGroupedRows } from "components/DataGrid/utils";
import { formatDateAndTime } from "components/utils/date";
import { formatBytes } from "components/utils/format";
import classNames from "classnames";
import { EmptyStatePlaceholder } from "components/EmptyStatePlaceholder";
import { HoverCard } from "../../../HoverCard";
import { OverlayProps } from "@restart/ui/Overlay";

const FileNameSize = ({ file, isMobile }: { file: ApplicationFile; isMobile: boolean }) => {
    return (
        <div
            id={`${file.fileId}-name-size`}
            className={classNames("d-flex align-items-center gap-2 fs-7", {
                "lh-sm": isMobile,
            })}
        >
            <div className="py-1 px-2 border me-md-2 rounded-1 bg-white">
                <FontAwesomeIcon fontSize="1.5rem" icon={["fal", getFileIcon(file.fileName)]} />
            </div>
            <div className="d-flex flex-column gap-1 text-break">
                <span className="fw-bold">{file.fileName}</span>
                <span aria-hidden={true} className="text-body opacity-70">
                    <span className="me-1 fw-bold text-nowrap">File Size:</span>
                    <span className="text-nowrap">{formatBytes(Number(file.fileSize))}</span>
                </span>
                <span className="visually-hidden">{formatBytes(Number(file.fileSize), 2, true)}</span>
            </div>
        </div>
    );
};

const DownloadFileButton = ({
    file,
    onDownloadFile,
    downloadingFile,
    isMobile,
}: {
    file: ApplicationFile;
    onDownloadFile: (id: string, name: string) => void;
    downloadingFile?: string;
    isMobile: boolean;
}) => (
    <SubmitButton
        className={classNames("text-primary text-decoration-none text-nowrap", {
            "px-0": isMobile,
        })}
        onClick={() => {
            onDownloadFile(file.fileId, file.fileName);
        }}
        variant="link"
        spinnerText="Downloading..."
        disabled={isInIframe()}
        isSubmitting={downloadingFile === file.fileId}
        aria-describedby={`${file.fileId}-name-size`}
        title="Download File"
    >
        {downloadingFile !== file.fileId && <FontAwesomeIcon icon={["fal", "file-arrow-down"]} className="me-2" />}
        Download File
    </SubmitButton>
);

const TooltipFolderEncrypted = (id: string) => {
    return (
        <Tooltip placement="bottom" className="in encrypted-file-tooltip fs-7">
            <div id={id}>Encrypted files will not be downloaded.</div>
        </Tooltip>
    );
};

const TooltipFileEncrypted = <Popover className="py-2 px-3 fs-7">Encrypted files cannot be viewed or downloaded.</Popover>;

export function ApplicationFiles({ files, applicationNumber, setErrorSummary }: ApplicationFilesProps) {
    const hoveredRowRef = useRef<HTMLTableRowElement | null>(null);
    const [hoverMouseX, setHoverMouseX] = useState<number | null>(null);
    const [hoveredFile, setHoveredFile] = useState<ApplicationFile | null>(null);
    const isMobile = useMediaQuery("(max-width: 575px)");
    const [downloadingFile, setDownloadingFile] = useState<string>();
    const [downloadingFolder, setDownloadingFolder] = useState<{ [key: string]: boolean }>({});
    const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});

    const FILE_DETAILS_ID = "file-details-id";

    const onDownloadFile = async (fileId: string, fileName: string) => {
        try {
            setDownloadingFile(fileId);
            const response = await downloadFile(fileId);
            download(response?.blob, fileName);
        } catch (error) {
            setErrorSummary(error);
        } finally {
            setDownloadingFile(undefined);
        }
    };

    const onDownloadFileFolder = async (appNumber: string, folder: string) => {
        if (isInIframe()) {
            return;
        }

        try {
            setDownloadingFolder({ ...downloadingFolder, [folder]: true });
            const response = await downloadFileFolder(appNumber, folder);
            download(response?.blob, `${folder}.zip`);
        } catch (error) {
            setErrorSummary(error);
        } finally {
            setDownloadingFolder({ ...downloadingFolder, [folder]: false });
        }
    };

    const collapseFolder = (folder: string) => {
        setCollapsed({ ...collapsed, [folder]: !collapsed[folder] });
    };

    const collapseFolderEnter = (e: KeyboardEvent<HTMLDivElement>, folder: string) => {
        e.key === "Enter" && collapseFolder(folder);
    };

    const rowGroups = getGroupedRows({
        rowGroups: [{ key: "folder" }],
        rows: files ? orderBy(files, (f) => new Date(f.uploadDate), "desc") : [],
    });

    const hoverCardPopperConfig = useMemo(
        () =>
            ({
                modifiers: [
                    {
                        name: "offset",
                        options: {
                            offset: (): [number, number] => {
                                const ref = hoveredRowRef.current;
                                if (!ref || hoverMouseX == null) return [0, 0];
                                const r = ref.getBoundingClientRect();
                                const centerX = r.left + r.width / 2;
                                return [hoverMouseX - centerX, 8];
                            },
                        },
                    },
                ],
            } as OverlayProps["popperConfig"]),
        [hoverMouseX]
    );

    return (
        <div className="application-files d-flex flex-column gap-3">
            <h2 className="m-0">Files</h2>
            {files.length > 0 ? (
                <div className="d-flex flex-column gap-4p5">
                    {Object.keys(rowGroups).map((folder, index) => (
                        <div key={index}>
                            <header className="d-flex justify-content-end align-items-center border-2 border-bottom bg-body-secondary position-relative">
                                <div
                                    role="button"
                                    aria-label={`Folder ${folder}`}
                                    aria-controls={FILE_DETAILS_ID + `-${index}`}
                                    aria-expanded={!collapsed[folder]}
                                    tabIndex={0}
                                    onClick={() => collapseFolder(folder)}
                                    onKeyUp={(e) => collapseFolderEnter(e, folder)}
                                    className="d-flex w-100 h-100 px-3p5 align-items-center gap-2 position-absolute"
                                    data-metadata={JSON.stringify({ name: collapsed[folder] ? "Expand folder" : "Collapse folder" })}
                                >
                                    <div className="d-flex align-items-center gap-2 gap-md-3 me-auto text-truncate">
                                        <FontAwesomeIcon
                                            className="text-dark"
                                            size="xl"
                                            icon={["fal", collapsed[folder] ? "folder" : "folder-open"]}
                                        />

                                        <h3 className="m-0 text-dark text-truncate fs-4 lh-base" id={`folder-name-${index}`}>
                                            {capitalize(folder.toLowerCase())}
                                        </h3>
                                    </div>

                                    <FontAwesomeIcon
                                        className="text-primary"
                                        icon={["fal", collapsed[folder] ? "chevron-down" : "chevron-up"]}
                                        size="lg"
                                    />
                                </div>
                                <OverlayTrigger
                                    trigger={rowGroups[folder].some((f: ApplicationFile) => f.isEncrypted) ? ["hover", "focus"] : []}
                                    placement="bottom"
                                    overlay={TooltipFolderEncrypted(`folder-download-tooltip-${index}`)}
                                >
                                    <div className="position-relative z-1 me-5 pe-4">
                                        <SubmitButton
                                            variant="light"
                                            className={classNames("d-flex align-items-center gap-2 text-nowrap", {
                                                "p-2": isMobile,
                                            })}
                                            spinnerText="Downloading"
                                            isSubmitting={downloadingFolder[folder]}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDownloadFileFolder(applicationNumber, folder);
                                            }}
                                            aria-label={isMobile ? "Download all" : undefined}
                                            aria-describedby={`folder-download-tooltip-${index} folder-name-${index}`}
                                        >
                                            {!downloadingFolder[folder] && <FontAwesomeIcon icon={["fal", "folder-arrow-down"]} />}
                                            {isMobile ? "" : "Download all"}
                                        </SubmitButton>
                                    </div>
                                </OverlayTrigger>
                            </header>
                            <Collapse in={!collapsed[folder]}>
                                <div id={FILE_DETAILS_ID + `-${index}`}>
                                    {!isMobile ? (
                                        <div className="pt-2 pb-4 px-3p5">
                                            <Table className="bg-light">
                                                <caption className="visually-hidden">Files list</caption>
                                                <DataGridHead>
                                                    <tr className="fw-bold">
                                                        <th className="col-7">File</th>
                                                        <th className="col-3">Date</th>
                                                        <th className="col-2 opacity-0">Actions</th>
                                                    </tr>
                                                </DataGridHead>
                                                <tbody>
                                                    {rowGroups[folder].map((f: ApplicationFile) => (
                                                        <tr
                                                            key={f.fileId}
                                                            onMouseLeave={() => {
                                                                setHoveredFile(null);
                                                                setHoverMouseX(null);
                                                            }}
                                                            onMouseMove={(e) => setHoverMouseX(e.clientX)}
                                                            onMouseEnter={(e) => {
                                                                hoveredRowRef.current = e.currentTarget;
                                                                setHoveredFile(f);
                                                            }}
                                                        >
                                                            <td>
                                                                <FileNameSize file={f} isMobile={isMobile} />
                                                            </td>
                                                            <td>{formatDateAndTime(f.uploadDate)}</td>
                                                            <td>
                                                                {f.isEncrypted ? (
                                                                    <OverlayTrigger
                                                                        trigger={["hover", "focus"]}
                                                                        placement="left"
                                                                        overlay={TooltipFileEncrypted}
                                                                    >
                                                                        <div
                                                                            className="text-secondary text-nowrap p-2p5"
                                                                            style={{ fontSize: "var(--btn-font-size)" }}
                                                                        >
                                                                            <FontAwesomeIcon icon={["fal", "lock"]} className="me-2" />
                                                                            Encrypted File
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                ) : (
                                                                    <DownloadFileButton
                                                                        file={f}
                                                                        onDownloadFile={onDownloadFile}
                                                                        downloadingFile={downloadingFile}
                                                                        isMobile={isMobile}
                                                                    />
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                            {hoveredFile != null &&
                                                hoveredRowRef.current != null &&
                                                (hoveredFile.refId || hoveredFile.tag) && (
                                                    <HoverCard
                                                        target={hoveredRowRef.current}
                                                        popperConfig={hoverCardPopperConfig}
                                                        header={{
                                                            id: hoveredFile.fileId,
                                                            name: hoveredFile.fileName,
                                                            icon: ["fal", getFileIcon(hoveredFile.fileName)],
                                                            subtitleLabel: "File Size",
                                                            subtitleValue: formatBytes(Number(hoveredFile.fileSize)),
                                                        }}
                                                        list={[
                                                            {
                                                                label: "Reference ID",
                                                                value: hoveredFile.refId,
                                                            },
                                                            {
                                                                label: "Filetag",
                                                                value: hoveredFile.tag,
                                                            },
                                                        ]}
                                                    />
                                                )}
                                        </div>
                                    ) : (
                                        rowGroups[folder].map((f: ApplicationFile, index: number) => (
                                            <div
                                                key={index}
                                                className={classNames("pt-3 bg-white bg-opacity-10 fs-6", {
                                                    "pt-5": !!index,
                                                })}
                                            >
                                                <div className="border-bottom pb-2">
                                                    <FileNameSize file={f} isMobile={isMobile} />
                                                </div>
                                                <div className="d-flex align-items-center border-bottom p-2">
                                                    <div className="col-5 fw-bold">Date</div>
                                                    <span>{formatDateAndTime(f.uploadDate)}</span>
                                                </div>
                                                {f.refId && (
                                                    <div className="d-flex align-items-center border-bottom p-2">
                                                        <div className="col-5 fw-bold">Reference ID</div>
                                                        <span className="text-break">{f.refId}</span>
                                                    </div>
                                                )}
                                                {f.tag && (
                                                    <div className="d-flex align-items-center border-bottom p-2">
                                                        <div className="col-5 fw-bold">Filetag</div>
                                                        <span className="text-break">{f.tag}</span>
                                                    </div>
                                                )}
                                                <div className="d-flex align-items-center border-bottom p-2">
                                                    <div className="col-5 fw-bold">Actions</div>
                                                    {f.isEncrypted ? (
                                                        <OverlayTrigger trigger={["hover", "focus"]} overlay={TooltipFileEncrypted}>
                                                            <div
                                                                className="text-secondary py-1p5"
                                                                style={{ fontSize: "var(--btn-font-size)" }}
                                                            >
                                                                <FontAwesomeIcon icon={["fal", "lock"]} className="me-2" />
                                                                Encrypted File
                                                            </div>
                                                        </OverlayTrigger>
                                                    ) : (
                                                        <DownloadFileButton
                                                            file={f}
                                                            onDownloadFile={onDownloadFile}
                                                            downloadingFile={downloadingFile ?? ""}
                                                            isMobile={isMobile}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </Collapse>
                        </div>
                    ))}
                </div>
            ) : (
                <EmptyStatePlaceholder
                    placeholderImage={<FilesPlaceholder />}
                    title="No files yet"
                    description="This is where you'll find files submitted for this application."
                    className="py-5"
                />
            )}
        </div>
    );
}

interface ApplicationFilesProps {
    files: ApplicationFile[];
    applicationNumber: string;
    setErrorSummary: (error: any) => void;
}

function getFileIcon(name: string) {
    const extension: string = name.split(".").pop() as string;
    if (["jpeg", "png", "jpg", "gif", "svg"].includes(extension)) {
        return "file-image";
    } else if (["xlsx", "docx", "pdf", "pptx", "xls", "doc", "ppt", "txt"].includes(extension)) {
        return "file-lines";
    } else {
        return "file";
    }
}
